// ============================================================
// Compact mode preference
// ============================================================
// Per-admin density preference, persisted server-side via the shared
// admin preferences API. Components can read either:
//
//   1. The hook directly — for inline conditional class strings:
//        const { compact, cx } = useCompactMode();
//        <div className={cx("p-6", "p-4")} />
//
//   2. The `data-density` attribute on <html> — for CSS-only tweaks:
//        :root[data-density="compact"] .my-thing { ... }
//
// Default for new admins is "compact"; admins can opt back into the
// roomier "comfortable" density from Settings.
// ============================================================

import { useCallback, useEffect } from "react";
import { useUserPreference } from "./useUserPreference";

export const COMPACT_PREF_KEY = "ui.density";
export const COMPACT_LOCAL_STORAGE_KEY = "uiDensity";

export type Density = "compact" | "comfortable";
export const DEFAULT_DENSITY: Density = "compact";

function isValidDensity(v: unknown): v is Density {
  return v === "compact" || v === "comfortable";
}

/**
 * Per-user density preference. Mirrors the resolved value to the
 * `data-density` attribute on `document.documentElement` so global
 * CSS / Tailwind variants can react without prop drilling.
 *
 * Components that change layout based on density should use the
 * returned `cx(comfortableClass, compactClass)` helper to keep call
 * sites tidy and consistent.
 */
export function useCompactMode() {
  const { value: density, setValue: setDensity, isSaving } = useUserPreference<Density>({
    key: COMPACT_PREF_KEY,
    defaultValue: DEFAULT_DENSITY,
    validate: isValidDensity,
    localStorageKey: COMPACT_LOCAL_STORAGE_KEY,
    errorMessage: "Couldn't save your density preference. Reverted to the previous value.",
  });

  // Reflect the current density on <html> so global CSS rules
  // (e.g. `:root[data-density="compact"] .glass-card-content { ... }`)
  // can target it without passing props through every component.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.density = density;
  }, [density]);

  const compact = density === "compact";

  const cx = useCallback(
    (comfortableClass: string, compactClass: string) =>
      compact ? compactClass : comfortableClass,
    [compact]
  );

  const toggle = useCallback(
    () => setDensity(compact ? "comfortable" : "compact"),
    [compact, setDensity]
  );

  return { density, compact, setDensity, toggle, cx, isSaving };
}
